import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import i18n, { TranslationParams } from '@particular.cloud/i18n-js';
import { I18nContext } from './i18nProvider';

/**
 * usually you want to put your useText hook call within your JSX:
 * <p>{useText({ key: 'hello', values: { name: "Tony Stark" } })}</p>
 * the issue with that is that we will pass a new values object to the hook
 * everytime the JSX renders.
 * To avoid unnecessary queries,
 * we make sure to compare the params object with the previous one.
 */
const paramsDiffer = (oldParams: TranslationParams, newParams: TranslationParams) => {
    const primitivesDiffer =
        oldParams.key !== newParams.key ||
        oldParams.language !== newParams.language ||
        oldParams.isolate !== newParams.isolate;
    if (primitivesDiffer) {
        return true;
    }
    if (!oldParams.values || !newParams.values) {
        return oldParams.values !== newParams.values;
    }
    const newKeys = Object.keys(newParams.values);
    for (const key of newKeys) {
        const newValue = newParams.values[key];
        const oldValue = oldParams.values[key];
        if (Array.isArray(newValue) && Array.isArray(oldValue)) {
            if (newValue.length !== oldValue.length) {
                return true;
            }
            for (let i = 0; i < newValue.length; i++) {
                if (newValue[i] !== oldValue[i]) {
                    return true;
                }
            }
        } else if (oldValue !== newValue) {
            return true;
        }
    }
};

/**
 * useText: hook to query and populate your localized application text
 */
const useText = ({ key, language, values, isolate }: TranslationParams) => {
    const { config, published } = useContext(I18nContext);
    const [string, setString] = useState(i18n.t({ key, language, values, isolate }));
    const paramsRef = useRef<TranslationParams>({ key, language, values, isolate });
    const mountedRef = useRef(true);

    useEffect(() => {
        return () => {
            mountedRef.current = false;
        };
    }, []);

    const queryText = useCallback((params: TranslationParams) => {
        // update string when either parameter changes
        const value = i18n.t(params, (fetchedValue) => {
            // this callback is fired if config.updateStrategy = "fetchOnExpired"
            // it will be triggered if the cache has expired and the string is fetched from the server
            // see @particular.cloud/i18n-js for more details
            if (mountedRef.current) {
                setString(fetchedValue);
            }
        });
        setString(value);
        // make sure the dependency array stays empty
    }, []);

    useEffect(() => {
        if (!paramsDiffer(paramsRef.current, { key, language, values, isolate })) {
            return;
        }
        // query text again if params changed
        queryText({ key, language, values, isolate });
        // update reference for other useEffects
        paramsRef.current = { key, language, values, isolate };
    }, [key, language, values, isolate]);

    useEffect(() => {
        // query text again on config change as language might have changed
        queryText({ key, language, values, isolate });
    }, [config]);

    useEffect(() => {
        const { key, language, values, isolate } = paramsRef.current;
        // don't run this on first render (no published)
        if (published) {
            (async () => {
                const value = await i18n.fetchT({ key, language, values, isolate });
                if (mountedRef.current) {
                    setString(value);
                }
            })();
        }
    }, [published]);

    return string;
};

export default useText;
